import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

interface TooltipProps {
	content: React.ReactNode;
	children: React.ReactNode;
}

const VIEWPORT_MARGIN = 8;

export function Tooltip({ content, children }: TooltipProps) {
	const [open, setOpen] = useState(false);
	const [pos, setPos] = useState({ top: 0, left: 0, placement: "top" as "top" | "bottom" });
	const triggerRef = useRef<HTMLSpanElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	function updatePosition() {
		const rect = triggerRef.current?.getBoundingClientRect();
		if (rect) setPos({ top: rect.top, left: rect.left + rect.width / 2, placement: "top" });
	}

	function handleMouseEnter() {
		updatePosition();
		setOpen(true);
	}

	function handleClick(e: React.MouseEvent) {
		e.stopPropagation();
		updatePosition();
		setOpen((o) => !o);
	}

	useLayoutEffect(() => {
		if (!open) return;
		const panel = panelRef.current;
		const trigger = triggerRef.current;
		if (!panel || !trigger) return;

		const triggerRect = trigger.getBoundingClientRect();
		const panelRect = panel.getBoundingClientRect();

		let left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;
		left = Math.min(
			Math.max(left, VIEWPORT_MARGIN),
			window.innerWidth - panelRect.width - VIEWPORT_MARGIN,
		);

		const fitsAbove = triggerRect.top - panelRect.height - VIEWPORT_MARGIN > 0;
		const placement = fitsAbove ? "top" : "bottom";

		setPos((prev) => {
			if (prev.left === left && prev.placement === placement) return prev;
			return { ...prev, left, placement };
		});
	}, [open, content]);

	useEffect(() => {
		if (!open) return;
		function handleOutside(e: MouseEvent | TouchEvent) {
			if (triggerRef.current?.contains(e.target as Node)) return;
			setOpen(false);
		}
		function handleScroll() {
			setOpen(false);
		}
		document.addEventListener("click", handleOutside);
		document.addEventListener("touchstart", handleOutside);
		window.addEventListener("scroll", handleScroll, true);
		return () => {
			document.removeEventListener("click", handleOutside);
			document.removeEventListener("touchstart", handleOutside);
			window.removeEventListener("scroll", handleScroll, true);
		};
	}, [open]);

	return (
		<span
			ref={triggerRef}
			className={styles.trigger}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={() => setOpen(false)}
			onClick={handleClick}
		>
			{children}
			{open &&
				createPortal(
					<div
						ref={panelRef}
						className={[styles.tooltip, styles[pos.placement]].join(" ")}
						style={{ top: pos.top, left: pos.left }}
						onClick={(e) => e.stopPropagation()}
					>
						{content}
					</div>,
					document.body,
				)}
		</span>
	);
}
